import type { BlogPost } from '../types';

export const post: BlogPost = {
  "id": "497",
  "slug": "types-of-volcanoes-shield-stratovolcano-cinder-cone",
  "title": "Types of Volcanoes: Shield, Stratovolcano, and Cinder Cone Explained",
  "excerpt": "Not all volcanoes are the same. The lava that builds them decides their shape, their eruptions, and how dangerous they are. Here are the main types.",
  "date": "2026-06-22",
  "category": "Education",
  "image": "https://images.unsplash.com/photo-1619266465172-02a857c3556d?ixid=M3w5NjY4NzN8MHwxfHNlYXJjaHwxfHx2b2xjYW5vfGVufDF8MHx8fDE3ODIxMTYwNDF8MA&ixlib=rb-4.1.0&w=800&q=80&fit=crop",
  "readTime": "4 min",
  "author": "EarthGuessr Team",
  "tldr": "The three main types of volcano are shield volcanoes (broad and gently sloping, built by runny lava), stratovolcanoes (tall, steep cones built by sticky lava and explosive eruptions), and cinder cones (small, steep piles of ejected fragments). The lava’s thickness and gas content decide which one forms.",
  "content": [
    {
      "type": "paragraph",
      "text": "From a distance, every volcano looks roughly the same: a mountain that sometimes erupts. Up close, they could hardly be more different. A volcano’s shape, its slopes, and the violence of its eruptions are all set by one thing above all — the kind of lava it produces. Learn to read that, and the landscape starts telling you what to expect."
    },
    {
      "type": "heading",
      "text": "It All Comes Down to the Lava"
    },
    {
      "type": "paragraph",
      "text": "Two properties of magma control almost everything about a volcano. The first is viscosity, or how runny the lava is. Thin, fluid lava flows easily and spreads out; thick, sticky lava piles up and traps gas. The second is gas content. When sticky lava holds a lot of dissolved gas, pressure builds until it is released explosively. Runny, low-gas lava tends to ooze; thick, gas-rich lava tends to blow."
    },
    {
      "type": "heading",
      "text": "Shield Volcanoes: Broad and Gentle"
    },
    {
      "type": "paragraph",
      "text": "Shield volcanoes are built from thin, fluid basaltic lava that runs far before it cools. Because the lava spreads out rather than piling up, these volcanoes grow into broad, gently sloping domes that resemble a warrior’s shield laid on the ground. Their eruptions are usually relatively calm, with rivers and fountains of lava rather than violent blasts."
    },
    {
      "type": "paragraph",
      "text": "Hawaii’s Mauna Loa and Kilauea are the classic shields, and they are enormous: measured from their base on the sea floor, Hawaiian shields are among the largest mountains on the planet by volume. Iceland, sitting on a mid-ocean ridge, also builds shield volcanoes from the same kind of runny basalt."
    },
    {
      "type": "heading",
      "text": "Stratovolcanoes: Tall, Steep, and Dangerous"
    },
    {
      "type": "paragraph",
      "text": "Stratovolcanoes, also called composite volcanoes, are the picture-postcard volcanoes — tall, symmetrical cones with steep sides. They are built up in layers, alternating between sticky lava flows and beds of ash and rock thrown out in explosive eruptions. That sticky, gas-rich lava is what makes them so hazardous: pressure builds behind the thick magma until it releases in powerful, sometimes catastrophic blasts."
    },
    {
      "type": "paragraph",
      "text": "Mount Fuji, Vesuvius, Mount St. Helens, and Mount Rainier are all stratovolcanoes. They cluster along subduction zones, where one tectonic plate dives beneath another and feeds thick, gas-charged magma upward — which is why so many of them ring the Pacific in the chain of volcanoes known as the Ring of Fire."
    },
    {
      "type": "heading",
      "text": "Cinder Cones: Small but Steep"
    },
    {
      "type": "paragraph",
      "text": "Cinder cones, sometimes called scoria cones, are the smallest of the common volcanoes. They form when frothy blobs of lava are thrown into the air from a single vent, cool in flight, and rain back down as fragments that pile up into a steep, neat cone with a crater at the top. They are often short-lived, building quickly during a single eruptive episode and then going quiet."
    },
    {
      "type": "paragraph",
      "text": "One of the most famous, Parícutin in Mexico, famously burst to life in a farmer’s field in 1943 and grew into a sizeable cone within a year, giving scientists a rare chance to watch a volcano form from the very beginning."
    },
    {
      "type": "heading",
      "text": "Other Volcanic Forms"
    },
    {
      "type": "paragraph",
      "text": "Beyond the big three, the planet builds volcanoes in several other shapes:"
    },
    {
      "type": "list",
      "items": [
        "Lava domes: mounds of lava too thick to flow, which pile up over a vent and can collapse dangerously.",
        "Calderas: huge basins formed when a volcano empties its magma chamber and the ground collapses inward.",
        "Supervolcanoes: rare, enormous systems such as Yellowstone, capable of eruptions far larger than any in recorded history.",
        "Fissure vents: long cracks that erupt curtains of fluid lava, common in places like Iceland."
      ]
    },
    {
      "type": "heading",
      "text": "Reading a Volcano’s Shape"
    },
    {
      "type": "paragraph",
      "text": "Once you know the types, a volcano’s profile becomes a clue. A broad, gently domed mountain hints at runny basalt and gentle eruptions. A tall, steep, symmetrical cone warns of sticky lava and explosive potential. A small, sharply sloped hill with a neat crater is likely a cinder cone. The landscape, in other words, records the kind of fire that built it."
    }
  ]
};
